import UnsavedChangesBoundary, { DirtyState } from "../../components/common/UnsavedChangesBoundary";
import React, { useEffect, useState } from "react";
import { createControl, listControlAssignmentUsers } from "../../api/controls.api.js";
import { getRisks } from "../../riskService";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Modal from "../../ui/Modal";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import ControlEffectivenessBadge from "./ControlEffectivenessBadge";
import { useControlEffectivenessConfig } from "../../hooks/useControlEffectivenessConfig";
import { buildControlEffectivenessOptions } from "../../utils/controlEffectiveness";
import { colors, radius, spacing, typography } from "../../ui/tokens";

const CONTROL_LABEL_OPTIONS = ["Preventative", "Detective", "Corrective"];

const createDefaults = (defaultRiskId) => ({
  name: "",
  description: "",
  risk_ids: defaultRiskId ? [String(defaultRiskId)] : [],
  status: "Draft",
  category: "",
  owner_user_id: "",
  assignee_user_id: "",
  control_labels: [],
  catalog_id: "",
  effectiveness_rating: "",
  last_review_date: "",
  last_reviewed_by: "",
  review_note: "",
});

export default function ControlCreateModal({ open, onClose, onCreated, defaultRiskId }) {
  const [form, setForm] = useState(() => createDefaults(defaultRiskId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [riskPickerValue, setRiskPickerValue] = useState("");
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState(null);
  const [riskOptions, setRiskOptions] = useState([]);
  const [risksLoading, setRisksLoading] = useState(false);
  const [risksError, setRisksError] = useState(null);
  const { items: effectivenessConfig } = useControlEffectivenessConfig();

  useEffect(() => {
    if (!open) return;
    setForm(createDefaults(defaultRiskId));
    setError(null);
    setFieldErrors({});
    setRiskPickerValue("");
  }, [defaultRiskId, open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const loadOwners = async () => {
      setOwnersLoading(true);
      setOwnersError(null);
      try {
        const res = await listControlAssignmentUsers();
        const users = res?.data || [];
        const options = users
          .filter((u) => u?.username)
          .map((u) => ({
            value: String(u.id),
            username: u.username,
            label: u.username,
          }));
        if (active) {
          setOwnerOptions(options);
        }
      } catch (err) {
        if (active) {
          setOwnerOptions([]);
          setOwnersError(err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Unable to load users.");
        }
      } finally {
        if (active) {
          setOwnersLoading(false);
        }
      }
    };
    loadOwners();
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const loadRisks = async () => {
      setRisksLoading(true);
      setRisksError(null);
      try {
        const res = await getRisks({ limit: 200 });
        const payload = res?.data || res;
        const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
        const options = items
          .filter((r) => r?.id)
          .map((r) => ({
            value: String(r.id),
            label: r.name ? `${r.name} (#${r.id})` : `Risk #${r.id}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        const defaultId = defaultRiskId ? String(defaultRiskId) : "";
        if (defaultId && !options.some((opt) => opt.value === defaultId)) {
          options.unshift({ value: defaultId, label: `Risk #${defaultId}` });
        }
        if (active) {
          setRiskOptions(options);
        }
      } catch (err) {
        if (active) {
          setRiskOptions([]);
          setRisksError(err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Unable to load risks.");
        }
      } finally {
        if (active) {
          setRisksLoading(false);
        }
      }
    };
    loadRisks();
    return () => {
      active = false;
    };
  }, [defaultRiskId, open]);

  const update = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "effectiveness_rating" && !value) {
        next.last_review_date = "";
        next.last_reviewed_by = "";
      }
      return next;
    });
    setFieldErrors((current) => {
      if (!current[key] && !(key === "effectiveness_rating" && (current.last_review_date || current.last_reviewed_by))) {
        return current;
      }
      const next = { ...current };
      delete next[key];
      if (key === "effectiveness_rating") {
        delete next.last_review_date;
        delete next.last_reviewed_by;
      }
      return next;
    });
    setError(null);
  };

  const addRiskSelection = (riskId) => {
    if (!riskId) return;
    setForm((f) => {
      if (f.risk_ids.includes(riskId)) return f;
      return { ...f, risk_ids: [...f.risk_ids, riskId] };
    });
  };

  const removeRiskSelection = (riskId) => {
    setForm((f) => ({ ...f, risk_ids: f.risk_ids.filter((id) => id !== riskId) }));
  };

  const onSubmit = async () => {
    const nextFieldErrors = {};
    if (!form.name || form.risk_ids.length === 0) {
      setError("Name and at least one risk are required");
      return;
    }
    if (form.effectiveness_rating) {
      if (!form.last_review_date) {
        nextFieldErrors.last_review_date = "Provide last review date when setting effectiveness.";
      }
      if (!form.last_reviewed_by) {
        nextFieldErrors.last_reviewed_by = "Provide reviewer when setting effectiveness.";
      }
      if (!form.review_note.trim()) {
        nextFieldErrors.review_note = "Provide a review note when setting effectiveness.";
      }
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("Complete the review details to set an effectiveness rating.");
      return;
    }
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      await createControl({
        name: form.name,
        description: form.description || undefined,
        risk_ids: form.risk_ids.map((id) => Number(id)),
        status: form.status || undefined,
        category: form.category || undefined,
        owner_user_id: form.owner_user_id || undefined,
        assignee_user_id: form.assignee_user_id || undefined,
        control_labels: Array.isArray(form.control_labels) ? form.control_labels : [],
        catalog_id: form.catalog_id || undefined,
        effectiveness_rating: form.effectiveness_rating || undefined,
        last_review_date: form.effectiveness_rating ? form.last_review_date || undefined : undefined,
        last_reviewed_by: form.effectiveness_rating ? form.last_reviewed_by || undefined : undefined,
        review_note: form.effectiveness_rating ? form.review_note.trim() : undefined,
      });
      onCreated && onCreated();
      onClose && onClose();
      setForm({
        name: "",
        description: "",
        risk_ids: [],
        status: "Draft",
        category: "",
        owner_user_id: "",
        assignee_user_id: "",
        control_labels: [],
        catalog_id: "",
        effectiveness_rating: "",
        last_review_date: "",
        last_reviewed_by: "",
        review_note: "",
      });
      setRiskPickerValue("");
    } catch (err) {
      setError(err?.message || "Failed to create control");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const submitDisabled = loading || !form.name || form.risk_ids.length === 0;
  const effectivenessOptions = buildControlEffectivenessOptions(effectivenessConfig);

  return (
    <UnsavedChangesBoundary onClose={onClose} itemName="control">
      {(requestClose) => (
    <Modal
      open={open}
      onClose={requestClose}
      title="Create Control"
      description="Create a control and link it to at least one risk."
      size="lg"
      contentStyle={modalBodyStyle}
      footerStyle={modalFooterStyle}
      footer={
        <>
          <Button onClick={requestClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onSubmit} variant="primary" disabled={submitDisabled}>
            {loading ? "Creating..." : "Create"}
          </Button>
        </>
      }
    >
      <div style={formLayoutStyle}>
        <DirtyState isDirty={JSON.stringify(form) !== JSON.stringify(createDefaults(defaultRiskId))} />
        <section style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionTitleStyle}>Required information</h3>
            <p style={sectionDescriptionStyle}>Name and at least one linked risk are required.</p>
          </div>
          <div style={sectionContentStyle}>
            <Input
              label="Name *"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Control name"
              style={controlStyle}
            />
            <Select
              label="Risk *"
              value={riskPickerValue}
              onChange={(e) => {
                const selected = e.target.value;
                setRiskPickerValue("");
                addRiskSelection(selected);
              }}
              style={controlStyle}
              disabled={risksLoading || riskOptions.length === 0}
            >
              <option value="">Select a risk</option>
              {riskOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            {risksError && riskOptions.length === 0 && <div style={inlineErrorStyle}>{risksError}</div>}
            {form.risk_ids.length > 0 && (
              <div style={chipWrapStyle}>
                {form.risk_ids.map((riskId) => {
                  const match = riskOptions.find((opt) => String(opt.value) === String(riskId));
                  const label = match?.label || `Risk #${riskId}`;
                  return (
                    <span key={riskId} style={chipStyle}>
                      {label}
                      <button
                        type="button"
                        onClick={() => removeRiskSelection(riskId)}
                        aria-label={`Remove ${label}`}
                        style={chipRemoveButtonStyle}
                      >
                        X
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionTitleStyle}>Description</h3>
          </div>
          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Describe the control objective and scope."
            style={{ ...controlStyle, minHeight: 90 }}
          />
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionTitleStyle}>Ownership</h3>
            <p style={sectionDescriptionStyle}>Optional accountability and assignment metadata.</p>
          </div>
          <div style={twoColumnGridStyle}>
            <Select
              label="Owner"
              value={form.owner_user_id}
              onChange={(e) => update("owner_user_id", e.target.value)}
              style={controlStyle}
              disabled={ownersLoading || ownerOptions.length === 0}
            >
              <option value="">Select owner (optional)</option>
              {ownerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <Select
              label="Assignee"
              value={form.assignee_user_id}
              onChange={(e) => update("assignee_user_id", e.target.value)}
              style={controlStyle}
              disabled={ownersLoading || ownerOptions.length === 0}
            >
              <option value="">Select assignee (optional)</option>
              {ownerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          {ownersError && ownerOptions.length === 0 && <div style={inlineErrorStyle}>{ownersError}</div>}
          <p style={helperTextStyle}>
            Owner is accountable for the control; Assignee handles day-to-day execution.
          </p>
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionTitleStyle}>Classification</h3>
            <p style={sectionDescriptionStyle}>Optional categorization fields and control labels.</p>
          </div>
          <div style={twoColumnGridStyle}>
            <Input
              label="Category"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              placeholder="Category"
              style={controlStyle}
            />
            <Input
              label="Catalog ID"
              type="text"
              value={form.catalog_id}
              onChange={(e) => update("catalog_id", e.target.value)}
              placeholder="Catalog ID"
              style={controlStyle}
            />
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => update("status", e.target.value)}
              style={controlStyle}
            >
              {["Draft", "Open", "In Progress", "Closed"].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </div>
          <div style={checkboxFieldStyle}>
            <span style={fieldHeadingStyle}>Control labels</span>
            <div style={checkboxGroupStyle}>
              {CONTROL_LABEL_OPTIONS.map((label) => {
                const selected = Array.isArray(form.control_labels) && form.control_labels.includes(label);
                return (
                  <label key={label} style={checkboxItemStyle}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {
                        const current = Array.isArray(form.control_labels) ? form.control_labels : [];
                        const next = selected
                          ? current.filter((item) => item !== label)
                          : [...current, label];
                        update("control_labels", next);
                      }}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionTitleStyle}>Governance</h3>
            <p style={sectionDescriptionStyle}>Effectiveness is optional, but it requires review details under the current backend rule.</p>
          </div>
          <div style={twoColumnGridStyle}>
            <div style={{ display: "grid", gap: spacing.xs }}>
              <Select
                label="Effectiveness rating"
                value={form.effectiveness_rating}
                onChange={(e) => update("effectiveness_rating", e.target.value)}
                options={effectivenessOptions}
                style={controlStyle}
              />
              <ControlEffectivenessBadge value={form.effectiveness_rating} config={effectivenessConfig} />
            </div>
            {form.effectiveness_rating && (
              <>
                <Input
                  label="Last review date"
                  type="date"
                  value={form.last_review_date}
                  onChange={(e) => update("last_review_date", e.target.value)}
                  error={fieldErrors.last_review_date}
                  style={controlStyle}
                />
                <Select
                  label="Last reviewed by"
                  value={form.last_reviewed_by}
                  onChange={(e) => update("last_reviewed_by", e.target.value)}
                  error={fieldErrors.last_reviewed_by}
                  style={controlStyle}
                >
                  <option value="">Select reviewer</option>
                  {ownerOptions.map((opt) => (
                    <option key={opt.value} value={opt.username}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                <Textarea
                  label="Review note"
                  value={form.review_note}
                  onChange={(e) => update("review_note", e.target.value)}
                  error={fieldErrors.review_note}
                  style={controlStyle}
                />
              </>
            )}
          </div>
          {form.effectiveness_rating && (
            <p style={helperTextStyle}>
              Review date, reviewer and rationale are required when you set effectiveness.
            </p>
          )}
        </section>

        {error && (
          <div role="alert" style={errorBannerStyle}>
            {error}
          </div>
        )}
      </div>
    </Modal>
      )}
    </UnsavedChangesBoundary>
  );
}

const modalBodyStyle = {
  paddingTop: spacing.lg,
};

const modalFooterStyle = {
  flexWrap: "wrap",
  rowGap: spacing.sm,
};

const formLayoutStyle = {
  display: "grid",
  gap: spacing.md + spacing.sm,
};

const sectionStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  padding: spacing.md + spacing.sm,
  display: "grid",
  gap: spacing.md,
};

const sectionHeaderStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const sectionTitleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
};

const sectionDescriptionStyle = {
  margin: 0,
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const sectionContentStyle = {
  display: "grid",
  gap: spacing.md,
};

const twoColumnGridStyle = {
  display: "grid",
  gap: spacing.md,
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const controlStyle = {
  width: "100%",
};

const inlineErrorStyle = {
  color: colors.danger,
  fontSize: typography.size.xs,
};

const errorBannerStyle = {
  color: colors.danger,
  background: colors.dangerSubtle,
  border: `1px solid ${colors.dangerBorder}`,
  borderRadius: radius.sm,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.xs,
};

const helperTextStyle = {
  margin: 0,
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const chipWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.md}px`,
  borderRadius: radius.pill,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.text,
  fontSize: typography.size.xs,
  maxWidth: "100%",
};

const chipRemoveButtonStyle = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: typography.size.xs,
  padding: 0,
  lineHeight: 1,
  color: colors.textMuted,
};

const checkboxFieldStyle = {
  display: "grid",
  gap: spacing.sm,
};

const checkboxGroupStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.sm,
};

const checkboxItemStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: `${spacing.xs}px ${spacing.sm}px`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.pill,
  background: colors.surface,
  fontSize: typography.size.sm,
  color: colors.text,
};

const fieldHeadingStyle = {
  fontSize: typography.size.sm,
  color: colors.text,
  fontWeight: typography.weight.medium,
};
